import { useAccountSelector, WalletData } from '@/design-system/components/accountSelector/useAccountSelector';
import { AlertType, UniAlert } from '@/design-system/components/alerts';
import { UniButton } from '@/design-system/components/buttons/UniButton';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { ModalHeader } from '@/design-system/components/modal/ModalHeader';
import { H6, TextBody, TextCaption } from '@/design-system/components/typography';
import { AttachmentViewer } from '@/features/bill-payment/screens/components/attachmentViewer/AttachmentViewer';
import { DATE_FORMAT, formatDate } from '@/utils/dateFormatting';
import { StackActions, useNavigation } from '@react-navigation/native';
import { LayoutContext } from 'contexts/LayoutContext';
import { Pressable, ScrollView, SxProp, useDripsyTheme, View } from 'dripsy';
import * as DocumentPicker from 'expo-document-picker';
import _, { isString } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutChangeEvent, useWindowDimensions } from 'react-native';
import { useStore } from 'store/store';
import { RootStackRoutes } from 'types';
import { IntervalBillPayment, OtpConfirmType, ScheduleBillPayment } from '../../enum';
import { useBillPaymentForm } from '../../hooks/useBillPaymentForm.hook';
import { PAYMENT_TYPE, QuoteDataRequest, QuoteResponse, useCreateBillPayment } from '../../hooks/useCreateBillPayment.hook';
import { BillPaymentStackRoutes } from '../../payment.navigator';
import { displayAmountWithCurrency, openImage64, openURL } from '../../utils/utils';
import { BillPaymentInfoItem } from '../components/BillPaymentInfoItem';
import { errorParser } from '../otp/type';

export interface BillPaymentConfirmationProps {
  request: QuoteDataRequest;
  response: QuoteResponse;
  debitAccount: string;
  onClose: () => void;
  flagUploaded?: boolean;
  documentSelected?: DocumentPicker.DocumentResult;
  paymentType: PAYMENT_TYPE;
}

export const BillPaymentConfirmationWeb: React.FC<BillPaymentConfirmationProps> = (props) => {
  const { request, response, debitAccount, onClose, flagUploaded, documentSelected, paymentType } = props;
  const { t } = useTranslation('global-bill-payment');
  const navigation = useNavigation();
  const layout = useContext(LayoutContext);
  const { height: heightScreen } = useWindowDimensions();
  const [sizeWidthButton, setSizeWithButton] = useState<number>(0);
  const [debitAccountApi, setDebitAccountApi] = useState<string>('');
  const paddingVerticalContainer = 30;
  const {
    general: { alertMessage, setAlertMessage, getThumbnailUrLFromResponse },
    postCreateBillPayment: { postSubmit, mapInstructionSubmitRequest, isLoadingSubmit }
  } = useCreateBillPayment();

  const {
    detailForm: { setAssetName, setAmount },
    general: { debitAmount, billPaymentFee, userFee, isLoadingFee }
  } = useBillPaymentForm();

  const store = useStore();
  const { theme } = useDripsyTheme();
  const { $4 } = theme.space || ({} as any);
  const {
    topHeader,
    body,
    viewBodyRow,
    viewBodyPadding,
    viewRowButton,
    textMerchant,
    textSuperMerchant,
    textBillAmount,
    textAmount,
    viewButton2,
    textReturn,
    viewDividerHorizontal,
    viewAlert
  } = sxStyles;

  useEffect(() => {
    if (!!response && !!response.attributes) {
      setAmount(response.attributes.amount!.toString());
      setAssetName(response.attributes.asset!);
    }
  }, [response]);

  const onValueChange = (value: string) => {};
  const onWalletChange = (value: WalletData | undefined) => {
    if (value) {
      setDebitAccountApi(`${value.label}\n${value.accountNumber}`);
    }
  };

  const {} = useAccountSelector({
    asset: response?.attributes?.asset,
    value: response?.attributes?.source?.attributes?.walletId,
    type: 'currency',
    onValueChange,
    onWalletChange
  });

  const onConfirm = () => {
    if (store.users && (store.users.tfaKeyStatus || store.users.phoneTfaKeyStatus)) {
      onClose();
      setTimeout(() => {
        navigation.navigate(RootStackRoutes.BillPayment, {
          screen: BillPaymentStackRoutes.OtpConfirmation,
          params: {
            request: request,
            response: response,
            flagUploaded: flagUploaded,
            debitAccount: !_.isEmpty(debitAccount) ? debitAccount : debitAccountApi,
            documentSelected,
            mode: store.users && store.users.tfaKeyStatus ? OtpConfirmType.GOOGLE_OTP : OtpConfirmType.SMS_OTP,
            paymentType,
            userFee
          }
        });
      }, 350);
    } else {
      setAlertMessage('');
      postSubmit(
        mapInstructionSubmitRequest(request, response, null),
        (submitData) => {
          onClose();
          setTimeout(() => {
            navigation.dispatch(
              StackActions.replace(RootStackRoutes.BillPayment, {
                screen: BillPaymentStackRoutes.Success,
                params: {
                  request,
                  response,
                  debitAccount: !_.isEmpty(debitAccount) ? debitAccount : debitAccountApi,
                  submitResponse: submitData.data,
                  documentSelected,
                  paymentType,
                  userFee
                }
              })
            );
          }, 500);
        },
        (error) => {
          const data: any = error.response?.data;
          const errorMessage = data && isString(data.message) ? errorParser(data.message, t) : errorParser(error.message, t);
          setAlertMessage(errorMessage);
        }
      );
    }
  };

  const navigationToForm = () => {
    onClose();
    setTimeout(() => {
      navigation.navigate(RootStackRoutes.BillPayment, {
        screen: BillPaymentStackRoutes.Form,
        params: {
          request,
          response,
          documentSelected,
          paymentType
        }
      });
    }, 400);
  };

  const onCancel = () => {
    onClose();
  };

  const onLayout = (e: LayoutChangeEvent) => {
    const { width } = e.nativeEvent.layout;
    setSizeWithButton(width);
  };

  const renderScheduleDate = () => {
    if (_.isEmpty(response?.attributes?.schedule)) {
      return <></>;
    }

    return (
      <>
        {response?.attributes?.schedule &&
        response?.attributes.schedule.type?.toLowerCase() !== ScheduleBillPayment.RECURRING.toLowerCase() ? (
          <BillPaymentInfoItem
            title={t('global-bill-payment::glossary.scheduled-date')}
            description={
              !!response.attributes?.schedule?.endDate
                ? formatDate(response.attributes?.schedule?.endDate, { format: DATE_FORMAT.NO_TIME })
                : ' - '
            }
          />
        ) : (
          <BillPaymentInfoItem
            title={t('global-bill-payment::glossary.date-of-first-payment')}
            description={
              !!response.attributes?.schedule?.startDate
                ? formatDate(response.attributes?.schedule?.startDate, { format: DATE_FORMAT.NO_TIME })
                : ' - '
            }
          />
        )}
      </>
    );
  };

  const renderDebitAccount = () => {
    if (!_.isEmpty(debitAccount)) {
      return <BillPaymentInfoItem title={t('global-bill-payment::glossary.debit-account')} description={debitAccount} />;
    }
    if (!_.isEmpty(debitAccountApi)) {
      return <BillPaymentInfoItem title={t('global-bill-payment::glossary.debit-account')} description={debitAccountApi} />;
    }
    return <BillPaymentInfoItem title={t('global-bill-payment::glossary.debit-account')} description={'-'} />;
  };

  const renderImage = () => {
    if (!_.isEmpty(getThumbnailUrLFromResponse(response))) {
      return (
        <Pressable onPress={async () => openURL(getThumbnailUrLFromResponse(response))}>
          <AttachmentViewer width={'100%'} height={392} imageUrl={getThumbnailUrLFromResponse(response)} />
        </Pressable>
      );
    }
    if (
      documentSelected &&
      documentSelected.type === 'success' &&
      (documentSelected.mimeType == 'image/png' || documentSelected.mimeType == 'image/jpeg')
    ) {
      return (
        <Pressable onPress={() => openImage64(documentSelected.uri)}>
          <AttachmentViewer width={'100%'} height={392} imageUrl={documentSelected.uri} />
        </Pressable>
      );
    }
    return <></>;
  };

  return (
    <View
      sx={{
        height: 'auto',
        width: layout.dimensions.width,
        maxWidth: Math.min(850, layout.dimensions.width),
        maxHeight: heightScreen - paddingVerticalContainer
      }}>
      <View sx={topHeader}>
        <ModalHeader title={t(`global-bill-payment::confirmation.title--bill-payment-confirmation`)} closeModal={onClose} bordered />
      </View>
      <ScrollView>
        <View sx={body}>
          <TextBody semiBold>{t('global-bill-payment::confirmation.title--please-confirm')}</TextBody>
          <View sx={viewBodyRow}>
            <View sx={{ flex: 444 }}>
              <TextCaption semiBold sx={textMerchant}>
                {t('global-bill-payment::glossary.merchant-bank-name')}
              </TextCaption>
              <H6 bold sx={textSuperMerchant}>
                {response?.attributes?.destination?.attributes?.recipient}
              </H6>
              <TextCaption semiBold sx={textBillAmount}>
                {t('global-bill-payment::glossary.bill-amount')}
              </TextCaption>
              <H6 sx={textAmount} bold>
                {displayAmountWithCurrency(response.attributes?.amount?.toString() ?? '', response.attributes?.asset!)}
              </H6>
              <BillPaymentInfoItem
                title={t('global-bill-payment::glossary.bill-bank-account-number')}
                description={response?.attributes?.destination?.attributes?.accountNumber ?? '-'}
              />
              <BillPaymentInfoItem
                title={t('global-bill-payment::glossary.payment-additional-details')}
                description={
                  _.isEmpty(response?.attributes?.destination?.attributes?.paymentInfo)
                    ? '-'
                    : response?.attributes?.destination?.attributes?.paymentInfo ?? ''
                }
              />
              {renderDebitAccount()}
              <BillPaymentInfoItem
                title={t('global-bill-payment::glossary.instruction-fee')}
                description={`${billPaymentFee}`}
                descriptionSx={{ color: '$danger' }}
              />
              <BillPaymentInfoItem
                title={t('global-bill-payment::glossary.debit-amount')}
                description={debitAmount}
                descriptionSx={{ color: '$danger' }}
              />
              {renderScheduleDate()}
              {!_.isEmpty(response?.attributes?.schedule?.interval) && (
                <BillPaymentInfoItem
                  title={t('global-bill-payment::glossary.frequency')}
                  description={
                    response?.attributes?.schedule?.interval === IntervalBillPayment.DAILY
                      ? t('global-bill-payment::glossary.daily')
                      : response?.attributes?.schedule?.interval !== IntervalBillPayment.WEEKLY
                      ? t('global-bill-payment::glossary.monthly')
                      : t('global-bill-payment::glossary.weekly')
                  }
                />
              )}
              {response?.attributes?.schedule &&
                response?.attributes?.schedule.type?.toLowerCase() === ScheduleBillPayment.RECURRING.toLowerCase() && (
                  <BillPaymentInfoItem
                    title={t('global-bill-payment::glossary.payment-end-date')}
                    description={
                      !!response.attributes?.schedule?.endDate
                        ? formatDate(response.attributes?.schedule?.endDate, { format: DATE_FORMAT.NO_TIME })
                        : ' - '
                    }
                  />
                )}
              <BillPaymentInfoItem
                title={t('global-bill-payment::glossary.remarks')}
                description={_.isEmpty(response.remarks) ? '-' : response.remarks!}
              />
            </View>
            <View sx={viewBodyPadding} />
            <View sx={{ flex: 322 }}>{renderImage()}</View>
          </View>
          {!_.isEmpty(alertMessage) && <UniAlert type={AlertType.Error} message={alertMessage} sx={viewAlert} />}
        </View>
      </ScrollView>
      {!flagUploaded ? (
        <View sx={viewRowButton} onLayout={onLayout}>
          <View sx={{ flexDirection: 'row', alignItems: 'center', width: (sizeWidthButton - $4) / 2 }}>
            <UniButton
              variant={ButtonVariant.outline1}
              labelSx={textReturn}
              sx={{ ...viewButton2, ...{ width: ((sizeWidthButton - $4) / 2 - $4) / 2 } }}
              label={t('global-bill-payment::glossary.cancel')}
              onPress={onCancel}
            />
            <View sx={viewDividerHorizontal} />
            <UniButton
              variant={ButtonVariant.outline1}
              labelSx={textReturn}
              sx={{ ...viewButton2, ...{ width: ((sizeWidthButton - $4) / 2 - $4) / 2 } }}
              label={t('global-bill-payment::glossary.edit-information')}
              onPress={navigationToForm}
            />
          </View>
          <View sx={viewDividerHorizontal} />
          <UniButton
            sx={{ ...viewButton2, ...{ width: (sizeWidthButton - $4) / 2 } }}
            label={t('global-bill-payment::general.place-instruction')}
            onPress={onConfirm}
            loading={isLoadingSubmit}
            disabled={isLoadingSubmit || isLoadingFee}
          />
        </View>
      ) : (
        <View sx={viewRowButton} onLayout={onLayout}>
          <UniButton
            variant={ButtonVariant.outline1}
            labelSx={textReturn}
            sx={{ ...viewButton2, ...{ width: (sizeWidthButton - $4) / 2 } }}
            label={t('global-bill-payment::glossary.cancel')}
            onPress={onCancel}
          />
          <View sx={viewDividerHorizontal} />
          <UniButton
            sx={{ ...viewButton2, ...{ width: (sizeWidthButton - $4) / 2 } }}
            label={t('global-bill-payment::general.place-instruction')}
            onPress={onConfirm}
            loading={isLoadingSubmit}
            disabled={isLoadingSubmit || isLoadingFee}
          />
        </View>
      )}
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    flex: 1,
    paddingX: '$4',
    backgroundColor: '$backgroundPrimary'
  },
  topHeader: {
    height: '$17',
    justifyContent: 'center',
    zIndex: 3
  },
  body: {
    pt: '$7',
    px: '$7',
    zIndex: 2
  },
  viewBodyRow: {
    width: '100%',
    flexDirection: 'row',
    mt: '$5',
    mb: '$5'
  },
  viewBodyPadding: {
    width: '$7'
  },
  viewRowButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    mt: '$5',
    mb: '$7',
    mx: '$7'
  },
  textMerchant: {
    mb: '$1'
  },
  textSuperMerchant: {
    color: '$secondary',
    mb: '$5'
  },
  textBillAmount: {
    mb: '$1'
  },
  textAmount: {
    color: '$secondary',
    mb: '$5',
    lineHeight: '$5'
  },
  viewButton2: {
    width: '$60',
    height: '$11'
  },
  textReturn: {
    color: '$textPrimary'
  },
  viewDividerHorizontal: {
    width: '$4'
  },
  viewAlert: {
    mt: '$1'
  }
};
